import { ChangeEvent, FC, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiCheckbox,
  MiDetailFields,
  MiSectionHeader,
} from "@miplatform/components";
import { INPUT_TYPES } from "@miplatform/constants";
import {
  AccountType,
  IAccountResponse,
  IBuilderDuration,
  IStepTemplateData,
} from "@miplatform/types";
import { getHoursAndMinutes, toMinutes } from "@miplatform/utils";
import { useAccounts } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";

// time validation helpers
const isValidHours = (hours: number) => hours >= 0 && hours <= 23;
const isValidMinutes = (minutes: number) => minutes >= 0 && minutes <= 59;

const FORM_FIELD_OPTIONS = {
  shouldDirty: true,
  shouldValidate: true,
} as const;

const findBuilderDuration = (
  builderDurations: IBuilderDuration[],
  builderId: string
) => builderDurations.find((b) => b.builderId === builderId);

const upsertBuilderDuration = (
  builderDurations: IBuilderDuration[],
  entry: IBuilderDuration
): IBuilderDuration[] => {
  const exists = builderDurations.some((b) => b.builderId === entry.builderId);
  return exists
    ? builderDurations.map((b) =>
        b.builderId === entry.builderId ? { ...b, ...entry } : b
      )
    : [...builderDurations, entry];
};

const removeBuilderDuration = (
  builderDurations: IBuilderDuration[],
  builderId: string
) => builderDurations.filter((b) => b.builderId !== builderId);

const updateBuilderDurationTime = (
  builderDurations: IBuilderDuration[],
  builderId: string,
  duration: number
) =>
  builderDurations.map((b) =>
    b.builderId === builderId ? { ...b, duration } : b
  );

const clearBuilderDuration = (
  builderDurations: IBuilderDuration[],
  builderId: string
): IBuilderDuration[] =>
  builderDurations.map((b) => {
    if (b.builderId !== builderId) {
      return b;
    }
    const { duration: _, ...rest } = b;
    return rest;
  });

const buildBuilderDurationRows = (
  accounts: IAccountResponse[],
  builderDurations: IBuilderDuration[],
  hasBuilderDurationsError: boolean
): BuilderDurationRow[] =>
  accounts.map((account) => {
    const builder = findBuilderDuration(builderDurations, account.id);
    const { hours, minutes } = getHoursAndMinutes(builder?.duration);
    const hasError =
      builder?.duration != null &&
      builder.duration === 0 &&
      hasBuilderDurationsError;

    return {
      account,
      builder,
      hours,
      minutes,
      hasError,
      isStepDisabledForBuilder: builder?.isStepDisabledForBuilder ?? false,
    };
  });

export const WorkOrderStepDurationTabView: FC<
  WorkOrderStepDurationTabViewProps
> = ({ form, extraTabProps = {} }) => {
  const { isReadOnly } = extraTabProps;
  const {
    watch,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = form;

  const { duration = 0, builderDurations = [] } = watch();
  const { classes } = useStyles();
  const { accounts, getAccounts } = useAccounts({
    accountTypes: [AccountType.Builder],
  });

  const builderDurationRows = buildBuilderDurationRows(
    accounts,
    builderDurations,
    !!errors.builderDurations
  );

  const updateBuilderDurations = (updated: IBuilderDuration[]) => {
    setValue(
      "builderDurations",
      updated.filter(({ duration }) => duration !== 0),
      FORM_FIELD_OPTIONS
    );
  };

  const areAllBuildersStepDisabled =
    accounts.length > 0 &&
    accounts.every(
      (account) =>
        findBuilderDuration(builderDurations, account.id)
          ?.isStepDisabledForBuilder === true
    );

  const handleDisableStepForAllBuildersChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const isStepDisabledForBuilder = e.target.checked;
    const currentBuilderDurations = getValues("builderDurations") ?? [];
    const accountIds = new Set(accounts.map((account) => account.id));
    const otherBuilderDurations = currentBuilderDurations.filter(
      (bd) => !accountIds.has(bd.builderId)
    );

    if (isStepDisabledForBuilder) {
      const updatedAccountDurations = accounts.map((account) => ({
        ...findBuilderDuration(currentBuilderDurations, account.id),
        builderId: account.id,
        isStepDisabledForBuilder: true,
      }));

      updateBuilderDurations([
        ...otherBuilderDurations,
        ...updatedAccountDurations,
      ]);
      return;
    }

    const updatedAccountDurations = currentBuilderDurations.reduce<
      IBuilderDuration[]
    >((acc, existing) => {
      if (!accountIds.has(existing.builderId)) {
        return acc;
      }
      if (existing.duration == null) {
        return acc;
      }
      return [...acc, { ...existing, isStepDisabledForBuilder: false }];
    }, []);

    updateBuilderDurations([
      ...otherBuilderDurations,
      ...updatedAccountDurations,
    ]);
  };

  useEffect(() => {
    getAccounts();
  }, []);

  const defaultDurationField = () => {
    const { hours, minutes } = getHoursAndMinutes(duration);

    const fields = [
      {
        label: "Hours",
        inputProps: { type: "number" },
        formControl: control,
        disabled: isReadOnly,
        value: hours,
        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
          const newHours = Number(e.target.value);
          if (!isValidHours(newHours)) {
            return;
          }
          setValue("duration", toMinutes(newHours, minutes), {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      },
      {
        label: "Minutes",
        inputProps: { type: "number" },
        formControl: control,
        disabled: isReadOnly,
        value: minutes,
        required: true,
        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
          const newMinutes = Number(e.target.value);
          if (!isValidMinutes(newMinutes)) {
            return;
          }
          setValue("duration", toMinutes(hours, newMinutes), {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      } as any,
    ];

    return fields;
  };

  const getBuilderDurationFields = ({
    account,
    builder,
    hours,
    minutes,
    hasError,
    isStepDisabledForBuilder,
  }: BuilderDurationRow) => {
    const accountId = account.id;
    const hasCustomDuration =
      builder?.duration != null && builder?.duration > 0;

    return [
      {
        label: "Disable Step for Builder",
        fieldType: INPUT_TYPES.CHECKBOX,
        formControl: control,
        value: isStepDisabledForBuilder,
        disabled: isReadOnly,
        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
          if (e.target.checked) {
            updateBuilderDurations(
              upsertBuilderDuration(builderDurations, {
                builderId: accountId,
                isStepDisabledForBuilder: true,
              })
            );
          } else {
            updateBuilderDurations(
              removeBuilderDuration(builderDurations, accountId)
            );
          }
        },
      },
      {
        label: "Custom Duration",
        formControl: control,
        fieldType: INPUT_TYPES.CHECKBOX,
        disabled: isReadOnly,
        width: "20%",
        value: hasCustomDuration,
        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
          if (e.target.checked) {
            updateBuilderDurations(
              upsertBuilderDuration(builderDurations, {
                builderId: accountId,
                duration: duration || 0,
                isStepDisabledForBuilder: builder?.isStepDisabledForBuilder,
              })
            );
          } else {
            updateBuilderDurations(
              clearBuilderDuration(builderDurations, accountId)
            );
          }
        },
      },
      {
        label: "Hours",
        inputProps: hasCustomDuration ? { type: "number" } : undefined,
        disabled: isReadOnly || !hasCustomDuration,
        formControl: control,
        error: hasError,
        helperText: hasError && errors.builderDurations?.message,
        value: !hasCustomDuration ? "--" : hours,
        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
          const newHours = Number(e.target.value);
          if (!isValidHours(newHours)) {
            return;
          }
          updateBuilderDurations(
            updateBuilderDurationTime(
              builderDurations,
              accountId,
              toMinutes(newHours, minutes)
            )
          );
        },
        width: "20%",
      },
      {
        label: "Minutes",
        inputProps: hasCustomDuration ? { type: "number" } : undefined,
        disabled: isReadOnly || !hasCustomDuration,
        required: hasCustomDuration,
        formControl: control,
        error: hasError,
        helperText: hasError && errors.builderDurations?.message,
        value: !hasCustomDuration ? "--" : minutes,
        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
          const newMinutes = Number(e.target.value);
          if (!isValidMinutes(newMinutes)) {
            return;
          }
          updateBuilderDurations(
            updateBuilderDurationTime(
              builderDurations,
              accountId,
              toMinutes(hours, newMinutes)
            )
          );
        },
        width: "20%",
      } as any,
    ];
  };

  return (
    <ConfigAccordionHeader title="Step Duration">
      <Box className={classes.root}>
        <MiDetailFields
          headerProps={{
            title: "Default Step Template Duration",
            variant: "text",
          }}
          detailFields={defaultDurationField()}
        />
        <MiSectionHeader
          title="Set Duration Per Builder"
          variant="text"
          rightAction={
            <MiCheckbox
              label="Disable Step for All Builders"
              value={areAllBuildersStepDisabled}
              disabled={isReadOnly}
              handleChange={handleDisableStepForAllBuildersChange}
            />
          }
        />
        <Box className={classes.builderList}>
          {builderDurationRows.map((row) => (
            <Box key={row.account.id} className={classes.builderCard}>
              <Box className={classes.builderNameBox}>
                <Typography className={classes.builderName}>
                  {row.account.name}
                </Typography>
              </Box>
              <Box className={classes.builderFieldsBox}>
                <MiDetailFields detailFields={getBuilderDurationFields(row)} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </ConfigAccordionHeader>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    backgroundColor: theme.palette.grey.mediumLight,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
  },
  builderList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    margin: theme.spacing(1),
  },
  builderCard: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
    backgroundColor: theme.palette.common.white,
  },
  builderNameBox: {
    width: "30%",
  },
  builderFieldsBox: {
    width: "70%",
  },
  builderName: {
    color: theme.palette.primary.main,
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.fontSize.medium,
  },
}));

interface WorkOrderStepDurationTabViewProps {
  form: UseFormReturn<IStepTemplateData>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}

type BuilderDurationRow = {
  account: IAccountResponse;
  builder?: IBuilderDuration;
  hours: number;
  minutes: number;
  hasError: boolean;
  isStepDisabledForBuilder: boolean;
};
